import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DEFAULT_ROW_HEIGHT = 40;
const DEFAULT_HEADER_HEIGHT = 30;
const DEFAULT_OVERSCAN = 10;
const SCROLL_BOTTOM_THRESHOLD = 200;
const FOOTER_HEIGHT = 36;
const SCROLL_IDLE_MS = 60;
const ADD_COLUMN_WIDTH = 50;
const PLACEHOLDER_BAR = {
  height: 10,
  width: '55%',
  borderRadius: 4,
  backgroundColor: '#f1f3f5',
};

const colWidth = (col) => col.width || col.minWidth || 120;

const BASE_CELL = {
  display: 'flex',
  alignItems: 'center',
  padding: '0 10px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  height: '100%',
  flexShrink: 0,
  isolation: 'isolate',
};

/**
 * Virtualized table with pinned-left columns.
 */
const TasksVirtualizedTable = ({
  columns,
  dataSource,
  isLoading,
  skeletonSource,
  emptyText,
  onRowContextMenu,
  onScrollBottom,
  loadingMore,
  rowHeight = DEFAULT_ROW_HEIGHT,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  overscan = DEFAULT_OVERSCAN,
  headerAccessory,
}) => {
  const hasAccessory = !!headerAccessory;
  const { cols, leftCount, leftOffsets, totalWidth } = useMemo(() => {
    const visible = (columns || []).filter(
      (c) => !c.hidden && c.dataIndex && c.key !== undefined,
    );
    const left = visible.filter((c) => c.fixed === 'left');
    const middle = visible.filter(
      (c) => c.fixed !== 'left'/* && c.fixed !== 'right'*/,
    );
    // const right = visible.filter((c) => c.fixed === 'right');
    const all = [...left, ...middle/*, ...right*/];

    // Trailing header-only column hosting the add/remove-column control.
    // It carries no dataIndex, so body cells render empty for it.
    if (hasAccessory) {
      all.push({
        key: '__add_column__',
        width: ADD_COLUMN_WIDTH,
        minWidth: ADD_COLUMN_WIDTH,
        lightweight: true,
        accessory: true,
      });
    }

    const offsets = [];
    let acc = 0;
    for (let i = 0; i < left.length; i += 1) {
      offsets.push(acc);
      acc += colWidth(left[i]);
    }

    return {
      cols: all,
      leftCount: left.length,
      leftOffsets: offsets,
      totalWidth: all.reduce((s, c) => s + colWidth(c), 0),
    };
  }, [columns, hasAccessory]);

  const rows = useMemo(
    () => (isLoading ? skeletonSource || [] : dataSource || []),
    [dataSource, isLoading, skeletonSource],
  );

  const scrollerVisible = isLoading || rows.length > 0;

  const buildCellStyle = useCallback(
    (col, index, isHeader) => {
      const isLeft = index < leftCount;
      return {
        ...BASE_CELL,
        width: colWidth(col),
        borderRight:
          index === cols.length - 1 ? undefined : '1px solid #e5e7eb',
        borderBottom: '1px solid #e5e7eb',
        ...(isHeader
          ? {
              backgroundColor: '#ffffff',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '18px',
              color: '#5c5c5c',
              whiteSpace: 'nowrap',
            }
          : { backgroundColor: '#ffffff', fontSize: 14, color: '#434343' }),
        ...(isLeft
          ? { position: 'sticky', left: leftOffsets[index], zIndex: 1 }
          : {}),
      };
    },
    [cols.length, leftCount, leftOffsets],
  );

  const headerCellStyles = useMemo(
    () => cols.map((c, i) => buildCellStyle(c, i, true)),
    [cols, buildCellStyle],
  );
  const bodyCellStyles = useMemo(
    () => cols.map((c, i) => buildCellStyle(c, i, false)),
    [cols, buildCellStyle],
  );

  // Cache rendered row nodes by key. A window change only builds the rows
  // that actually entered the viewport; unchanged rows are reused by reference,
  // which lets React bail out of reconciling them entirely. Reset on anything
  // that affects a row's markup changes.
  const rowNodeCache = useMemo(
    () => new Map(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cols,
      bodyCellStyles,
      rowHeight,
      headerHeight,
      totalWidth,
      onRowContextMenu,
      isLoading,
    ],
  );

  const scrollRef = useRef(null);
  const rafRef = useRef(0);
  const lastTriggerScrollTopRef = useRef(-1);
  const [range, setRange] = useState({ start: 0, end: 0, vStart: 0, vEnd: 0 });
  const [viewportWidth, setViewportWidth] = useState(0);

  // Active-scroll tracking: while the user is scrolling, rows entering the
  // viewport render as cheap placeholders
  const [isScrolling, setIsScrolling] = useState(false);
  const isScrollingRef = useRef(false);
  const idleTimerRef = useRef(0);
  // `mountedKeysRef` holds the keys that rendered as real cells in the previous frame,
  // so already-visible rows never flip to a placeholder mid-scroll.
  const mountedKeysRef = useRef(new Set());

  const update = useCallback(() => {
    rafRef.current = 0;
    const el = scrollRef.current;
    if (!el) return;
    const st = el.scrollTop;
    const vStart = Math.max(0, Math.floor((st - headerHeight) / rowHeight));
    const vEnd = Math.min(
      rows.length,
      Math.max(0, Math.ceil((st + el.clientHeight - headerHeight) / rowHeight)),
    );
    const start = Math.max(0, vStart - overscan);
    const end = Math.min(rows.length, vEnd + overscan);
    setViewportWidth((prev) =>
      prev === el.clientWidth ? prev : el.clientWidth,
    );
    // Only triggers a render when the visible window actually moved.
    setRange((prev) =>
      prev.start === start &&
      prev.end === end &&
      prev.vStart === vStart &&
      prev.vEnd === vEnd
        ? prev
        : { start, end, vStart, vEnd },
    );

    if (
      onScrollBottom &&
      !isLoading &&
      el.scrollHeight - (st + el.clientHeight) <= SCROLL_BOTTOM_THRESHOLD &&
      Math.abs(st - lastTriggerScrollTopRef.current) >= rowHeight
    ) {
      lastTriggerScrollTopRef.current = st;
      onScrollBottom();
    }
  }, [headerHeight, rowHeight, overscan, rows.length, onScrollBottom, isLoading]);

  const handleScroll = useCallback(() => {
    if (!isScrollingRef.current) {
      isScrollingRef.current = true;
      setIsScrolling(true);
    }
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      setIsScrolling(false);
    }, SCROLL_IDLE_MS);
    if (!rafRef.current) rafRef.current = requestAnimationFrame(update);
  }, [update]);

  // Recompute the window before paint on mount / resize / data change.
  useLayoutEffect(() => {
    update();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => update());
    observer.observe(el);
    return () => observer.disconnect();
  }, [update, scrollerVisible]);

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(idleTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    lastTriggerScrollTopRef.current = -1;
  }, [rows.length]);

  if (!isLoading && rows.length === 0) {
    return (
      <div
        className="my-tasks-virtualized-table"
        style={{
          flex: '1 1 100%',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9ca3af',
          fontSize: 13,
        }}
      >
        {emptyText || 'No tasks'}
      </div>
    );
  }

  const total = rows.length;
  const startIndex = Math.min(range.start, total);
  const endIndex = Math.min(range.end, total);

  const mountedKeys = mountedKeysRef.current;
  const nextMounted = new Set();
  const visibleRows = [];
  for (let i = startIndex; i < endIndex; i += 1) {
    const row = rows[i];
    if (!row) continue;

    const rowStyle = {
      position: 'absolute',
      top: headerHeight + i * rowHeight,
      left: 0,
      height: rowHeight,
      width: totalWidth,
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
    };

    // A row's heavy cells render real if it was already mounted on the last frame, or —
    // when not scrolling — if it's actually on screen. Off-screen overscan rows
    // stay cheap placeholders, so a large overscan buffers the scroll without
    // making the "upgrade on stop" mount the whole window at once.
    const isVisible = i >= range.vStart && i < range.vEnd;
    const centerReal = mountedKeys.has(row.key) || (!isScrolling && isVisible);
    if (!centerReal) {
      visibleRows.push(
        <div key={row.key} className="mtv-row" style={rowStyle}>
          {cols.map((col, index) => (
            <div key={col.key} className="mtv-body-cell" style={bodyCellStyles[index]}>
              {index < leftCount || col.lightweight ? (
                row[col.dataIndex]
              ) : (
                <div style={PLACEHOLDER_BAR} />
              )}
            </div>
          ))}
        </div>,
      );
      continue;
    }

    nextMounted.add(row.key);
    let entry = rowNodeCache.get(row.key);
    if (!entry || entry.i !== i || entry.row !== row) {
      entry = {
        i,
        row,
        node: (
          <div
            key={row.key}
            className="mtv-row"
            style={rowStyle}
            onContextMenu={
              !isLoading && onRowContextMenu
                ? (e) => onRowContextMenu(row.key, e)
                : undefined
            }
          >
            {cols.map((col, index) => (
              <div key={col.key} className="mtv-body-cell" style={bodyCellStyles[index]}>
                {row[col.dataIndex]}
              </div>
            ))}
          </div>
        ),
      };
      rowNodeCache.set(row.key, entry);
    }
    visibleRows.push(entry.node);
  }
  mountedKeysRef.current = nextMounted;

  return (
    <div
      className="my-tasks-virtualized-table my-tasks-virtualized-scroll-host"
      ref={scrollRef}
      onScroll={handleScroll}
      style={{
        flex: '1 1 100%',
        minHeight: 0,
        overflow: 'auto',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: totalWidth,
          height:
            headerHeight + total * rowHeight + (loadingMore ? FOOTER_HEIGHT : 0),
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            display: 'flex',
            width: totalWidth,
            height: headerHeight,
            borderTop: '1px solid #e5e7eb',
            boxSizing: 'border-box',
          }}
        >
          {cols.map((col, index) => (
            <div key={col.key} style={headerCellStyles[index]}>
              {col.accessory ? headerAccessory : col.title}
            </div>
          ))}
        </div>
        {visibleRows}
        {loadingMore && (
          <div
            style={{
              position: 'absolute',
              top: headerHeight + total * rowHeight,
              left: 0,
              width: totalWidth,
              height: FOOTER_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
            }}
          >
            <div
              className="flex items-center justify-center gap-2"
              style={{
                position: 'sticky',
                left: 0,
                width: viewportWidth || '100%',
                height: '100%',
              }}
            >
              <div className="w-3.5 h-3.5 border-[1.5px] border-gray-200 border-t-blue-500 rounded-full animate-spin" />
              <span className="text-[12px] text-gray-400 font-medium">
                Loading more…
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TasksVirtualizedTable;
